import * as React from 'react';
import { Currency, OrganizationDeal, PaymentListEntryStatus, PaymentCycle, PaymentStatus, PrimaryDealType, DealFlag, DealFlags } from '../../../amorg-common';
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { AmorgAdminPageProvider } from '../AmorgAdminPageContext';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MessageCard } from '../../../designSystem/Message';
import { AdminSiteTabs } from '../_components/AdminSiteTabs';
import { PageSection } from '../../../designSystem/PageSection';
import { OrganizationView } from '../../../amorg-common';
import { createOrganizationDeal, deleteOrganizationDeal, getOrganizationById, updateOrganizationDeal } from '../../../api/adminClientApi';
import { Button } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { Table } from '../../../designSystem/Table';
import { TextInput } from '../../../designSystem/inputs/TextInput';
import { DateInput } from '../../../designSystem/inputs/DateInput';
import { Select } from '../../../designSystem/inputs/Select';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { FormField } from '../../../designSystem/formField/FormField';
import { TextField } from '../../../designSystem/formField/text/TextField';
import { SafeFlagSelector } from '../../../designSystem/compositeComponents/SafeFlagSelector';
import { DeleteConfirmationButton } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';

type PageState = {
    status: 'starting'
} |
{
    status: 'ready'
    loading: boolean,
    org: OrganizationView
}

export function AmorgAdminOrganizationDeals (): JSX.Element
{
    const [searchParams] = useSearchParams();

    const { organizationId } = useParams<{ organizationId: string; }>();
    const siteId = searchParams.get('siteId') || undefined;

    const [pageState, setPageState] = React.useState<PageState>({ status: 'starting' });
    const [error, setError] = React.useState('');

    const navigate = useNavigate();

    async function loadOrganization ()
    {
        if(!organizationId)
        {
            setError('Mangler orgId i URL');
            return;
        }

        setPageState(current => current.status === 'ready'
            ? { ...current, loading: true }
            : current);

        try
        {
            const org = await getOrganizationById(organizationId, () => navigate('/amorgy/login'));
            if(!org)
            {
                setError('Organisation ikke fundet: ' + organizationId);
                return;
            }

            setPageState({ status: 'ready', loading: false, org });
            setError('');
        }
        catch
        {
            setError('Organisationen kunne ikke indlæses');
        }
    }

    React.useEffect(() =>
    {
        loadOrganization();
    }, [organizationId]);

    const compareDeals = (a: OrganizationDeal, b: OrganizationDeal) =>
    {
        const aStart = a.start || new Date(0);
        const bStart = b.start || new Date(0);
        return aStart.getTime() - bStart.getTime();
    }

    let currentDeals: OrganizationDeal [] = [];
    if(pageState.status === 'ready')
    {
        currentDeals = pageState.org.deals.filter(d =>
            (d.start === null && d.end === null) ||
            (d.start === null && d.end !== null && d.end >= new Date()) ||
            (d.start !== null && d.start <= new Date() && d.end === null) ||
            (d.start !== null && d.start <= new Date() && d.end !== null && d.end >= new Date())
        ).sort(compareDeals);
    }

    let pastDeals: OrganizationDeal [] = [];
    if(pageState.status === 'ready')
    {
        pastDeals = pageState.org.deals.filter(d => !currentDeals
            .some(cd => cd.id === d.id))
            .sort(compareDeals);
    }

    const loading = pageState.status === 'starting' || pageState.loading;

    async function handleAddDeal ()
    {
        if(pageState.status !== 'ready')
            return;

        const result = await createOrganizationDeal(pageState.org.id, buildNewDeal(), () => navigate('/amorgy/login'));
        if(!result)
        {
            setError('Aftalen kunne ikke oprettes');
            return;
        }

        await loadOrganization();
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            {pageState.status === 'ready' &&

                <AmorgAdminPageProvider>
                    <SingleColumnLayout
                        noDivider
                        headerContainer={
                            <Flex flexDirection="column" gap={16}>
                                <AdminSiteTabs selectedAdminTab='sites' 
                                    selectedSiteTab='deals' 
                                    orgId={organizationId}
                                    siteId={siteId} />
                            </Flex>
                        }

                        contentContainer={
                            <Flex flexDirection='column' gap={32}>
                                {error && <MessageCard variant="error">{error}</MessageCard>}

                                <PageSection
                                    header={{
                                        headerVariant: 'H2',
                                        title:'Aktuelle Aftaler',
                                        defaultCollapsed: false,
                                        button:{
                                            variant: "filled",
                                            text: "Tilføj aftale",
                                            icon: "actions_plus",
                                            onClick: handleAddDeal
                                        }
                                    }}
                                    styleVariant="default-no-card"
                                >

                                    {currentDeals.length === 0 &&
                                        <Card variant="none">
                                            Ingen aktuelle aftaler
                                        </Card>
                                    }

                                    <Flex flexDirection="column" gap={16}>
                                        {currentDeals.map(deal =>
                                            <OrganizationDealCard
                                                key={deal.id}
                                                deal={deal}
                                                organizationId={pageState.org.id}
                                                onSaved={loadOrganization}
                                            />
                                        )}
                                    </Flex>
                                </PageSection>

                                {pastDeals.length > 0 &&
                                    <PageSection
                                        header={{
                                            title:'Tidligere Aftaler',
                                            defaultCollapsed: false
                                        }}
                                        styleVariant="default-no-card"
                                    >
                                        <Flex flexDirection="column" gap={16}>
                                            {pastDeals.map(deal =>
                                                <OrganizationDealCard
                                                    key={deal.id}
                                                    deal={deal}
                                                    organizationId={pageState.org.id}
                                                    requireUnlock
                                                    onSaved={loadOrganization}
                                                />
                                            )}
                                        </Flex>
                                    </PageSection>
                                }

                            </Flex>
                        } />
                </AmorgAdminPageProvider>

            }
        </>
    );
}


type OrganizationDealCardProps = {
    deal: OrganizationDeal,
    organizationId: string,
    requireUnlock?: boolean
    onSaved: () => Promise<void>
};

type DealPayment = OrganizationDeal['payment']['payments'][number];

function OrganizationDealCard (props:OrganizationDealCardProps): JSX.Element
{
    const [deal, setDeal] = React.useState(props.deal);
    const [locked, setLocked] = React.useState(props.requireUnlock || false);
    const [error, setError] = React.useState('');

    const navigate = useNavigate();

    React.useEffect(() => setDeal(props.deal), [props.deal]);

    async function saveDeal (nextDeal: OrganizationDeal)
    {
        setDeal(nextDeal);
        const saved = await updateOrganizationDeal(props.organizationId, nextDeal, () => navigate('/amorgy/login'));
        if(!saved)
        {
            setError('Aftalen kunne ikke gemmes');
            return;
        }

        setError('');
        await props.onSaved();
    }

    async function handleAddPayment ()
    {
        const payment: DealPayment = {
            time: new Date(),
            amount: 0,
            currency: deal.payment.currency,
            status: 'invoice-sent',
            note: ''
        };
        await saveDeal({ ...deal, payment: { ...deal.payment, payments: [...deal.payment.payments, payment] } });
    }

    function updatePayment (index: number, payment: DealPayment)
    {
        setDeal(current => ({
            ...current,
            payment: {
                ...current.payment,
                payments: current.payment.payments.map((item, itemIndex) => itemIndex === index ? payment : item)
            }
        }));
    }

    async function commitPayment (index: number, payment: DealPayment)
    {
        await saveDeal({
            ...deal,
            payment: {
                ...deal.payment,
                payments: deal.payment.payments.map((item, itemIndex) => itemIndex === index ? payment : item)
            }
        });
    }

    async function deletePayment (index: number)
    {
        await saveDeal({
            ...deal,
            payment: {
                ...deal.payment,
                payments: deal.payment.payments.filter((_, paymentIndex) => paymentIndex !== index)
            }
        });
    }

    async function handleDeleteDeal ()
    {
        const deleted = await deleteOrganizationDeal(props.organizationId, deal.id, () => navigate('/amorgy/login'));
        if(!deleted)
        {
            setError('Aftalen kunne ikke slettes');
            return;
        }

        setError('');
        await props.onSaved();
    }

    const payments = [...deal.payment.payments].sort((a, b) => a.time.getTime() - b.time.getTime());

    const disabled = locked;

    return (
        <Card variant="outlined">
            
            <Flex flexDirection="column" gap={16}>
                {error && <MessageCard variant="error">{error}</MessageCard>}

                <Flex>

                    <FlexItem flexGrow="1">
                        <EditableText
                            value={deal.title}
                            disabled={disabled}
                            onSave={(value) => saveDeal({ ...deal, title: value })}
                            ariaLabel="Aftaletitel" 
                            placeholder={'Aftalenavn'}
                            styleMode='H2'
                        />
                    </FlexItem>

                    <Flex justifyContent="flex-end" gap={8}>
                        <DeleteConfirmationButton
                            asIcon
                            buttonText="Slet aftale"
                            tooltipText="Slet aftale"
                            confirmationHeading="Slet aftale"
                            confirmationText={`Er du sikker på, at du vil slette aftalen "${deal.title}"?`}
                            requireKey={true}
                            disabled={disabled}
                            onConfirmDelete={handleDeleteDeal}
                        />

                        {props.requireUnlock &&
                            <Button variant="text" icon={locked ? 'lock' : 'lock_open'} onClick={() => setLocked(!locked)} />
                        }
                    </Flex>
                </Flex>
                
                <Flex>

                    <FlexItem flexGrow="1">
                        <Flex flexDirection="column" gap={8}>
                            <SelectField
                                label="Aftaletype"
                                minWidth='100%'
                                selectedValue={deal.dealType}
                                disabled={disabled}
                                onChange={value => saveDeal({ ...deal, dealType: value as PrimaryDealType })}
                                options={[
                                    { value: 'Lovservice', text: 'Lovservice' },
                                    { value: 'Lovpakken', text: 'Lovpakken' },
                                    { value: 'AMO Pakken', text: 'AMO Pakken' },
                                    { value: 'andet', text: 'Andet' }
                                ]}
                                width="160px"
                            />
                        
                            <SafeFlagSelector 
                                allowedFlags={DealFlags}
                                selectedFlags={deal.flags}
                                colorMode='colorIndex'
                                disabled={disabled}
                                onChange={(flags) => saveDeal({ ...deal, flags: flags as DealFlag[] })}
                            />
                        </Flex>
                    </FlexItem>

                    <Flex flexGrow="1" justifyContent="flex-end">
                        <FormField 
                            label="Periode"
                            inputComponent={
                                <div>
                                    <DateInput value={deal.start} disabled={disabled} showClearButton onValueChange={value => saveDeal({ ...deal, start: value })} />
                                    <DateInput value={deal.end} disabled={disabled} showClearButton onValueChange={value => saveDeal({ ...deal, end: value })} />
                                </div>
                            }
                        />
                    </Flex>

                </Flex>

                <Flex>
                </Flex>

                <Flex gap={16} alignItems="flex-end">
                    <TextField
                        label="Listepris" 
                        value={String(deal.payment.listPrice)} 
                        disabled={disabled} 
                        onChange={value => setDeal(current => ({ ...current, payment: { ...current.payment, listPrice: Number(value) } }))} 
                        onBlur={() => saveDeal(deal)} 
                        ariaLabel="Listepris"
                    />
                    <TextField
                        label="Aftalt pris"
                        value={String(deal.payment.agreedPrice)}
                        disabled={disabled}
                        onChange={value => setDeal(current => ({ ...current, payment: { ...current.payment, agreedPrice: Number(value) } }))}
                        onBlur={() => saveDeal(deal)}
                        ariaLabel="Aftalt pris"
                    />
                    <SelectField
                        label="Valuta"
                        selectedValue={deal.payment.currency} 
                        disabled={disabled} 
                        onChange={value => saveDeal({ ...deal, payment: { ...deal.payment, currency: value as Currency } })} 
                        options={[{ value: 'DKK', text: 'DKK' }, { value: 'EUR', text: 'EUR' }]} width="100px" />
                    <SelectField
                        label="Betalingscyklus"
                        selectedValue={deal.payment.cycle}
                        disabled={disabled}
                        onChange={value => saveDeal({ ...deal, payment: { ...deal.payment, cycle: value as PaymentCycle } })}
                        options={[{ value: 'monthly', text: 'Månedlig' }, { value: 'quarterly', text: 'Kvartalsvis' }, { value: 'yearly', text: 'Årlig' }]}
                        width="140px"
                    />
                </Flex>

                <Table fontSizePx={14}>
                    <thead>
                        <tr><th>Dato</th><th>Beløb</th><th>Valuta</th><th>Status</th><th>Note</th><th></th></tr>
                    </thead>
                    <tbody>
                        {payments.map(payment => {
                            const originalIndex = deal.payment.payments.indexOf(payment);
                            return <PaymentRow
                                key={`${payment.time.toISOString()}-${originalIndex}`}
                                payment={payment}
                                disabled={disabled}
                                onChange={nextPayment => updatePayment(originalIndex, nextPayment)}
                                onSave={nextPayment => commitPayment(originalIndex, nextPayment)}
                                onDelete={() => deletePayment(originalIndex)}
                            />;
                        })}
                    </tbody>
                </Table>

                {!disabled &&
                    <Button variant="text" text="Tilføj betaling" icon="actions_plus" onClick={handleAddPayment} />
                }
            </Flex>

        </Card>
    );
}

function PaymentRow (props: { payment: DealPayment, disabled?: boolean, onChange: (payment: DealPayment) => void, onSave: (payment: DealPayment) => void, onDelete: () => void }): JSX.Element
{
    return <tr>
           <td><DateInput value={props.payment.time} disabled={props.disabled} onValueChange={value => value && props.onSave({ ...props.payment, time: value })} /></td>
           <td><TextInput value={String(props.payment.amount)} disabled={props.disabled} onChange={value => props.onChange({ ...props.payment, amount: Number(value) })} onBlur={() => props.onSave(props.payment)} ariaLabel="Betalingsbeløb" /></td>
           <td><Select selectedValue={props.payment.currency} disabled={props.disabled} onChange={value => props.onSave({ ...props.payment, currency: value as Currency })} options={[{ value: 'DKK', text: 'DKK' }, { value: 'EUR', text: 'EUR' }]} width="85px" /></td>
           <td><Select selectedValue={props.payment.status} disabled={props.disabled} onChange={value => props.onSave({ ...props.payment, status: value as PaymentListEntryStatus })} options={[{ value: 'invoice-sent', text: 'Faktura sendt' }, { value: 'paid', text: 'Betalt' }]} width="130px" /></td>
           <td><TextInput value={props.payment.note} disabled={props.disabled} onChange={value => props.onChange({ ...props.payment, note: value })} onBlur={() => props.onSave(props.payment)} ariaLabel="Betalingsnote" /></td>
           <td><Button variant="destructive-text" icon="interface_actions_delete" toolTipText="Slet betaling" disabled={props.disabled} onClick={props.onDelete} /></td>
    </tr>;
}

function buildNewDeal (): Omit<OrganizationDeal, 'id'>
{
    return {
        title: 'Ny aftale',
        start: null,
        end: null,
        dealType: 'andet',
        flags: [],
        payment: {
            status: 'invoice-sent' as PaymentStatus,
            cycle: 'monthly',
            listPrice: 0,
            agreedPrice: 0,
            currency: 'DKK',
            reminderDate: null,
            payments: []
        }
    };
}